export default {
  id: 'behavior-analytics',
  title: 'Behavior Analytics',
  content: `## Behavior Analytics

Behavior Analytics tracks the **quality of your decision-making process** over time — not just your profit and loss.

You can have a winning month with poor behavioral analytics (got lucky with bad process) or a losing month with excellent analytics (sound process, bad luck). The analytics tell you which one is which.

---

## Where to Find It

**Analytics tab → Behavior**

---

## What It Tracks

### Decision Consistency

Are your Enter/Skip/Wait decisions consistent with what the setup signals suggest?

If you Enter on weak setups and Skip on strong ones, your decision consistency is low — even if some of those Enter decisions happened to make money.

---

### Plan Completeness

What % of your trades had a fully defined plan before entry — entry price, stop loss, target, and quantity all filled in?

| Rate | What it means |
|---|---|
| 90–100% | You plan every trade |
| 70–90% | Occasional gaps — check which fields you skip |
| Below 70% | You are trading without a plan regularly |

---

### R:R Discipline

What % of your trades had a reward:risk ratio of at least 2.0?

This is tracked separately from outcome. A 1.5:1 trade that made money still scores poorly here — because you accepted below-minimum R:R.

---

### Conviction Accuracy

When you marked a trade as HIGH conviction, how often was the outcome positive?

If your HIGH conviction trades perform the same as your LOW conviction trades, your conviction calibration is off. This is a sign of overconfidence or inconsistent analysis.

---

### WAIT Pattern

When you chose WAIT in ACT, you select reason tags explaining what you were waiting for. Over time, TradeLab tracks:

- Which tags appear most often in your WAITs
- Whether the confirmation you were waiting for actually arrived in the outcome

If you waited for "volume confirmation" 20 times but the outcome showed volume was already there, you are over-filtering.

---

## Behavioral Flags

TradeLab automatically flags patterns that indicate problematic behaviour:

| Flag | What triggers it |
|---|---|
| Revenge Trading | Low-quality trade placed within 30 min of a losing trade closing |
| Overtrading | Trade count significantly above your typical daily average |
| Stop Avoidance | Position held beyond stop loss level without an explicit update |
| FOMO Entry | Entry price far above signal price — chasing the move |
| Conviction Mismatch | HIGH conviction on a setup that scores below 60 on Setup Quality |

---

## Decision Quality Tiers

Your decisions are grouped into three tiers based on their composite ACT score:

| Tier | Score | What it means |
|---|---|---|
| High Quality | ≥ 80 | Disciplined, well-planned, signal-aligned |
| Medium Quality | 70–79 | Acceptable — some improvement areas |
| Low Quality | < 70 | Setup, risk, or decision had significant gaps |

Win rate is tracked separately for each tier. If your High Quality decisions have a meaningfully better win rate than Low Quality ones, your scoring is well-calibrated and your process is adding edge.

---

## Reading the Charts

The analytics section shows charts for:

- Score trend over time (rolling 7-day and 30-day average)
- Decision breakdown (% Enter / Wait / Skip per session)
- R:R distribution histogram
- Behavioral flag frequency over the last 90 days

Look at the trend, not any single session. A good week after a bad month is noise. A rising 30-day trend is signal.`,
};
